import {
  CASStatus,
  SharePointParentFolder,
  alphabeticalCompare,
} from '@cims-online/shared'
import { useSubmittalTrackerContext } from '../../../../SubmittalTrackerContext'
import { Submittal } from '../../../../SubmittalTrackerContext/types'
import { CIMSColumnsOptions } from 'components/CIMSTable/CIMSColumn/types'
import { getDeepestString } from 'utils/getReactNodeLength'
import { HelpMessage } from 'utils/constants'
import { useSingleUserColumn } from 'components/CIMSTable/common/hooks/useSingleUserColumn'
import { CIMSTag } from 'components/CIMSTag'
import { TableFilterItem } from 'hooks/useTableFilter/types'
import { useStatus } from './hooks/useStatus'
import { useRoutingGroups } from './hooks/useRoutingGroups'
import { dateFormatter } from 'utils/dateFormatter'
import { useExternalLinks } from 'components/CIMSTable/common/hooks/useExternalLinks'
const TrackerHelp = HelpMessage.CAS.SubmittalTracker

export const useColumns = (): CIMSColumnsOptions<Submittal> => {
  const { selectOptions, edit, refetch } = useSubmittalTrackerContext()
  return {
    prominentCustomFields: {
      disableEditWhen: ({ statusId, deleted }) => ({
        alreadyDisabled:
          statusId === CASStatus.Closed.id
            ? { why: 'invalid status', statusName: CASStatus.Closed.name }
            : false,
        recordInactive: deleted,
      }),
      onCheckBoolean: async ({ id }, input) => {
        await edit({ id, customFields: { booleanInputs: [input] } })
        refetch()
      },
      onCheckBooleans: async ({ id }, input) => {
        await edit({
          id,
          customFields: { listInputs: { checkboxes: [input] } },
        })
        refetch()
      },
    },
    columns: [
      useStatus(),
      useRoutingGroups(),
      useExternalLinks<Submittal>({
        parent: SharePointParentFolder.ConstructionAdminSubmittals,
        dataFn: ({
          id,
          externalLinks,
          sharePointFolderName,
          sharePointResource,
          packetSharePointResource,
        }) => ({
          recordId: id,
          externalLinks,
          folder: sharePointFolderName,
          resource: { webUrl: sharePointResource?.webUrl },
          packet: {
            name: packetSharePointResource?.name,
            webUrl: packetSharePointResource?.webUrl,
          },
        }),
      }),
      {
        title: 'Package Number',
        help: TrackerHelp.PackageNumber,
        render: ({ packageNumber }) => packageNumber,
        width: 225,
        sort: { with: 'key', key: 'packageNumber' },
        filter: { type: 'search', with: 'key', key: 'packageNumber' },
      },
      {
        title: 'Package Name',
        help: TrackerHelp.PackageName,
        render: ({ packageName }) => packageName,
        width: 210,
        filter: { type: 'search', with: 'key', key: 'packageName' },
        sort: { with: 'key', key: 'packageName' },
      },
      {
        title: 'Type',
        width: 150,
        help: TrackerHelp.Type,
        render: ({ type }) =>
          type && (
            <CIMSTag
              style={{ verticalAlign: 'top' }}
              tooltip={type.description}
            >
              {type.name}
            </CIMSTag>
          ),
        filter: {
          with: 'callback',
          items: selectOptions.typeOptions.reduce<TableFilterItem[]>(
            (items, { label, value }) => {
              if (!value) return items
              items.push({ text: getDeepestString(label), value })
              return items
            },
            []
          ),
          onFilter: (value, { type }) => type.id === value,
        },
        sort: {
          with: 'callback',
          onCompare: (a, b) => alphabeticalCompare(a.type, b.type, 'name'),
        },
      },
      {
        title: 'Subtype',
        help: TrackerHelp.Subtype,
        render: ({ subtype }) =>
          subtype && (
            <CIMSTag
              style={{ verticalAlign: 'top' }}
              tooltip={subtype.description}
            >
              {subtype.name}
            </CIMSTag>
          ),
        width: 150,
        filter: {
          type: 'default',
          with: 'callback',
          items: selectOptions.subtypeOptions.reduce<TableFilterItem[]>(
            (items, { label, value }) => {
              if (!value) return items
              items.push({ text: getDeepestString(label), value })
              return items
            },
            []
          ),
          onFilter: (value, { subtype }) => subtype?.id === value,
        },
        sort: {
          with: 'callback',
          onCompare: (a, b) =>
            alphabeticalCompare(a.subtype, b.subtype, 'name'),
        },
      },
      useSingleUserColumn({
        title: 'Submitted By',
        help: TrackerHelp.SubmittedBy,
        user: ({ submittedBy }) => submittedBy,
      }),
      {
        title: 'Created Date',
        help: TrackerHelp.CreatedDate,
        render: ({ createdDate }) => dateFormatter.simple(createdDate),
        width: 170,
        filter: { type: 'date', with: 'key', key: 'createdDate' },
        sort: {
          with: 'key',
          key: 'createdDate',
          default: 'descend',
          priority: 2,
        },
      },
      {
        title: 'Requested Review Date',
        help: TrackerHelp.RequestedReviewDate,
        render: ({ requestedReviewDate }) =>
          dateFormatter.simple(requestedReviewDate),
        width: 240,
        filter: { type: 'date', with: 'key', key: 'requestedReviewDate' },
        sort: { with: 'key', key: 'requestedReviewDate' },
      },
      {
        title: 'Contract Date',
        help: TrackerHelp.ContractualReviewDate,
        render: ({ contractualReviewDate }) =>
          dateFormatter.simple(contractualReviewDate),
        width: 175,
        filter: { type: 'date', with: 'key', key: 'contractualReviewDate' },
        sort: { with: 'key', key: 'contractualReviewDate' },
      },
      {
        title: 'Disposition',
        help: TrackerHelp.Disposition,
        render: ({ dispositionType }) =>
          dispositionType && (
            <CIMSTag
              style={{ verticalAlign: 'top' }}
              tooltip={dispositionType.description}
            >
              {dispositionType.name}
            </CIMSTag>
          ),
        width: 160,
        filter: {
          with: 'callback',
          items: (records) =>
            records.reduce((items, { dispositionType }) => {
              if (dispositionType)
                items.push({
                  text: dispositionType.name,
                  value: dispositionType.id,
                  tooltip: dispositionType.description,
                })
              return items
            }, Array<TableFilterItem>()),
          onFilter: (value, { dispositionType }) =>
            value === dispositionType?.id,
        },
        sort: {
          with: 'callback',
          onCompare: (a, b) =>
            alphabeticalCompare(a.dispositionType, b.dispositionType, 'name'),
        },
      },
      {
        title: 'Disposition Date',
        help: TrackerHelp.DispositionDate,
        render: ({ dispositionDate }) => dateFormatter.simple(dispositionDate),
        width: 190,
        filter: { type: 'date', with: 'key', key: 'dispositionDate' },
        sort: { with: 'key', key: 'dispositionDate' },
      },
      useSingleUserColumn({
        title: 'Disposition By',
        help: TrackerHelp.DispositionBy,
        user: ({ dispositionBy }) => dispositionBy,
      }),
    ],
  }
}
